/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ShowContactDetailsPropertyThunk } from "../../../../../redux-magic/thunks";

/* Components Imports */

import { CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Skeleton, Switch, Typography } from "@mui/material";
import BeegruButton from "../../../../common-components/buttons/BeegruButton";
import { ObjectId } from "bson";
import { Omit } from "lodash";
import { useRouter } from "next/router";
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../../models";
import StateInterface from "../../../../../redux-magic/state-interface";

/* Styled Components */

const MainContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "100%",
	gap: "0.5rem",
	padding: "1rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	borderRadius: "16px",
}));

const Heading = styled(Typography)(({ theme }) => ({
	margin: "0.5rem 0rem 0.5rem 0rem",
	width: "100%",
	fontWeight: "500",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const SwitchContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
}));

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

const ShowContactDetails = ({
	dispatch,
	session,
	loading,
	propertyData,
	property_created_by,
	profile_context,
}: {
	propertyData: any;
	dispatch: any;
	session: any;
	loading: boolean;
	property_created_by: LocalPropertyInterface["created_by"];
	profile_context: StateInterface["profile_context"];
}) => {
	const router = useRouter();

	const [switchLoading, setSwitchLoading] = React.useState<boolean>(false);

	/* Asked first (3 Nov 2026, the app's "Hide / Show your phone number?"): the switch only proposes the change. */
	const [pendingShow, setPendingShow] = React.useState<boolean | null>(null);

	const handleChange = (event: any) => setPendingShow(Boolean(event.target.checked));

	const applyChange = async (show: boolean) => {
		setPendingShow(null);
		setSwitchLoading(true);
		await dispatch(
			ShowContactDetailsPropertyThunk({
				property_id: propertyData._id,
				show_contact_details: show,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				profile_context: profile_context,
				business_profile_id: property_created_by.is_business_profile
					? property_created_by.business_profile_id != ""
						? (property_created_by.business_profile_id as BusinessProfileInterface)._id
						: ""
					: "",
			}),
		);
		setSwitchLoading(false);
	};

	if (loading) {
		return (
			<MainContainer>
				<Heading>
					<Skeleton
						animation="wave"
						variant="text"
						width="15rem"
					/>
				</Heading>
				<SwitchContainer>
					<Typography variant="body2">
						<Skeleton
							animation="wave"
							variant="text"
							width="15rem"
						/>
					</Typography>
				</SwitchContainer>
			</MainContainer>
		);
	}

	return (
		<React.Fragment>
			<Dialog
				open={pendingShow !== null}
				onClose={() => setPendingShow(null)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{pendingShow ? "Show your phone number?" : "Hide your phone number?"}</DialogTitle>
				<DialogContent>
					<DialogContentText>
						{pendingShow
							? "Your phone number will be visible to anyone viewing this listing, so buyers and agents can call you directly."
							: "Buyers and agents will no longer see your phone number on this listing. They can only reach you through lead forms or Beegru chat."}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<BeegruButton
						variant="outlined"
						flavor="primary"
						size="small"
						onClick={() => setPendingShow(null)}
					>
						Cancel
					</BeegruButton>
					<BeegruButton
						variant="contained"
						flavor="primary"
						size="small"
						onClick={() => pendingShow !== null && applyChange(pendingShow)}
					>
						{pendingShow ? "Show number" : "Hide number"}
					</BeegruButton>
				</DialogActions>
			</Dialog>
			<MainContainer>
				<Heading>{router.locale === "ar-AE" ? "إذن الاتصال" : "Show contact details"}</Heading>
				<SwitchContainer>
					<Typography variant="body2">
						{router.locale === "ar-AE"
							? "السماح للمستخدمين الآخرين برؤية تفاصيل الاتصال الخاصة بك"
							: "Allow other users to see your contact details"}
					</Typography>
					{switchLoading ? (
						<CircularProgress />
					) : (
						<Switch
							id="spp_show_contact_switch"
							checked={propertyData.show_contact_details}
							onChange={handleChange}
						/>
					)}
				</SwitchContainer>
			</MainContainer>
		</React.Fragment>
	);
};

export default ShowContactDetails;
